import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Smartphone } from 'lucide-react'
import { HAS_APP_LINKS, detectMobileOS, preferredAppUrl } from '@/config/appLinks'

/**
 * A slim banner shown only on mobile browsers, suggesting the native app.
 * Rendered on public pages and — more prominently — on the exam screen,
 * where the app gives a far better test-taking experience.
 *
 * Hidden entirely on desktop, when no store links are configured, or once
 * the user dismisses it (remembered for the session).
 *
 * `variant="prominent"` is a centered card (for the exam page); the default
 * is a dismissible top strip.
 */
export default function MobileAppBanner({ variant = 'strip' }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(
    () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem('app_banner_dismissed') === '1',
  )

  const os = detectMobileOS()
  const url = preferredAppUrl()
  if (!HAS_APP_LINKS || !os || !url || dismissed) return null

  function dismiss() {
    setDismissed(true)
    try { sessionStorage.setItem('app_banner_dismissed', '1') } catch { /* ignore */ }
  }

  const label = os === 'ios'
    ? t('app.openInAppStore', "App Store'da ochish")
    : t('app.openInPlay', "Google Play'da ochish")

  if (variant === 'prominent') {
    return (
      <div className="rounded-2xl p-4 flex items-center gap-3"
        style={{ background: 'var(--coral-tint)', border: '1px solid var(--coral-soft)' }}>
        <Smartphone size={22} style={{ color: 'var(--coral)' }} className="flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold m-0" style={{ color: 'var(--ink)' }}>
            {t('app.betterInApp', 'Ilovada qulayroq')}
          </p>
          <p className="text-[12px] m-0" style={{ color: 'var(--ink-2)' }}>
            {t('app.examBetterInApp', "Imtihonni 5mi.uz ilovasida ishlash tavsiya etiladi.")}
          </p>
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex-shrink-0 text-[13px] font-semibold px-3.5 py-2 rounded-xl text-white"
          style={{ background: 'var(--coral)', textDecoration: 'none' }}>
          {t('app.open', 'Ochish')}
        </a>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5"
      style={{ background: 'var(--bg-soft)', borderBottom: '1px solid var(--border)' }}>
      <Smartphone size={18} style={{ color: 'var(--coral)' }} className="flex-shrink-0" />
      <span className="text-[13px] flex-1 min-w-0" style={{ color: 'var(--ink-2)' }}>
        {t('app.availableOnMobile', "5mi.uz mobil ilovasi mavjud")}
      </span>
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="text-[13px] font-semibold whitespace-nowrap"
        style={{ color: 'var(--coral)', textDecoration: 'none' }}>
        {label}
      </a>
      <button onClick={dismiss} className="p-1 flex-shrink-0"
        style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-3)' }}
        aria-label={t('common.close', 'Yopish')}>
        <X size={16} />
      </button>
    </div>
  )
}
